import React, { useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import useUiuxStore from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { ShowSuccessFetch, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import { getHRM3050_R01List } from '../HRM3050_R01/Api_HRM3050_R01';
import {
    downloadExcel_HRM2120_R01,
    getHRM2120_R01AllCount,
    getHRM2120_R01CalendarList,
    getHRM2120_R01DetailList,
    getHRM2120_R01List,
    patchHRM2120_R01,
    postHRM2120_R01Bulk,
} from './Api_HRM2120_R01';
import {
    defaultHRM2120_R01DetailDto,
    HRM2120_R01DetailDto,
    HRM2120_R01BulkType,
    HRM2120_R01HeaderDto,
    HRM2120_R01HeaderGridRow,
} from './Dto_HRM2120_R01';
import { useHRM2120_R01Store } from './Store_HRM2120_R01';
import { END_DT_OPEN, getHRM2120_R01FirstDateOfMonth, validateHRM2120_R01ShiftList } from './Util_HRM2120_R01';

/** 사원·이력 그리드 참조와 하단 메시지 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2120_R01HeaderGridRow> | null>; // 사원 그리드
    detailGridRef: React.RefObject<AgGridReact<HRM2120_R01DetailDto> | null>; // 배정 이력 그리드
    setMessage: (value: string) => void; // 하단 메시지 설정
}

/** 일괄 배정 확인창에 표시할 방식 이름이다. */
const BULK_TYPE_NAMES: Record<HRM2120_R01BulkType, string> = {
    FIX: '고정 근무조',
    ROT: '2교대 A/B 번갈아',
    ROTA: '2교대 A조',
    ROTB: '2교대 B조',
};

/** 사원 조회, 배정 이력 저장, 일괄 배정, 달력 및 Excel 동작을 연결한다. */
export const HRM2120_R01Content = ({ gridRef, detailGridRef, setMessage }: Props) => {
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    useFetchActionbarAuth('HRM2120_R01');

    /** 확인창을 닫고 이전 확인창 속성을 비운다. */
    const closeConfirmModal = useCallback(() => {
        setConfirmModalVisible(false);
        setConfirmModalProps(undefined);
    }, [setConfirmModalProps, setConfirmModalVisible]);

    /** 사용 중인 근무조 목록을 조회한다. 배정 방식과 교대조 코드는 공통 Store가 제공한다. */
    const loadWorkShifts = useCallback(async () => {
        const response = await getHRM3050_R01List('search', {});
        if (!response.data.successFlag) return;

        const state = useHRM2120_R01Store.getState();
        state.setAllWorkShiftCodes(response.data.data.map(row => row.shiftCd));
        state.setWorkShiftList(response.data.data.filter(row => row.useYn === 'Y'));
    }, []);

    useEffect(() => {
        void loadWorkShifts();
    }, [loadWorkShifts]);

    /** 선택 사원의 저장된 이력과 5주 달력을 조회한다. */
    const loadDetails = useCallback(async (empNo?: string, baseDt?: string) => {
        const state = useHRM2120_R01Store.getState();
        if (!empNo) {
            state.setHRM2120_R01DetailList([]);
            state.setOriginalHRM2120_R01DetailList([]);
            state.setHRM2120_R01CalendarList([]);
            return;
        }

        const [detail, calendar] = await Promise.all([
            getHRM2120_R01DetailList(empNo),
            getHRM2120_R01CalendarList(empNo, baseDt || state.HRM2120_R01PastFilter.baseDt),
        ]);
        if (!detail.data.successFlag || !calendar.data.successFlag) return;

        state.setHRM2120_R01DetailList(detail.data.data);
        state.setOriginalHRM2120_R01DetailList(detail.data.data);
        state.setHRM2120_R01CalendarList(calendar.data.data);
    }, []);

    /** 검색 조건으로 사원·건수를 조회하고 직전 사원 또는 첫 사원을 선택한다. */
    const executeDataFetch = useCallback(async (option: 'search' | 'refetch', empNo?: string, quiet = false) => {
        setLoadingSpinner(true);
        try {
            const state = useHRM2120_R01Store.getState();
            const filter = state.HRM2120_R01Filter;
            const params = {
                compCd: filter.compCd || undefined,
                orgCd: filter.orgCd || undefined,
                empSts: filter.empSts || undefined,
                keyword: filter.keyword || undefined,
                baseDt: filter.baseDt,
            };
            const previousEmpNo = empNo || state.selectedHRM2120_R01?.empNo;
            if (option === 'search') state.setScrollTop(null);

            const [rows, count] = await Promise.all([
                getHRM2120_R01List(option, params),
                getHRM2120_R01AllCount(params),
            ]);
            if (!rows.data.successFlag) return;

            state.setHRM2120_R01List(rows.data.data);
            const current = useHRM2120_R01Store.getState();
            const selected = current.HRM2120_R01List.find(row => row.empNo === previousEmpNo)
                || current.HRM2120_R01List[0];
            const totalCount = count.data.successFlag ? count.data.data : rows.data.data.length;

            current.setSelectedHRM2120_R01(selected);
            current.setAllCount(totalCount);
            current.setHRM2120_R01PastFilter(filter);
            await loadDetails(selected?.empNo, filter.baseDt);

            if (quiet) return;
            if (rows.data.data.length === 0) {
                showWarning('데이터가 없습니다.');
                return;
            }
            ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${rows.data.data.length}/${totalCount}건)`);
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, loadDetails, setMessage]);

    /** 배정 이력의 수정·추가·삭제 표시가 있으면 true이다. */
    const hasChanges = useCallback((): boolean => {
        const state = useHRM2120_R01Store.getState();
        return state.deletedHRM2120_R01DetailList.length > 0
            || state.HRM2120_R01DetailList.some(row => row.isModified || (row.inserted && !!row.startDt));
    }, []);

    /** 저장하지 않은 변경을 버릴지 공통 확인창에서 묻는다. */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) {
            onConfirm();
            return;
        }

        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                closeConfirmModal();
                onConfirm();
            },
            onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, closeConfirmModal, setConfirmModalProps, setConfirmModalVisible]);

    /** 다른 사원을 선택하면 그 사원의 저장된 이력과 달력을 조회한다. */
    const handleHeaderRowClick = useCallback((row: HRM2120_R01HeaderDto) => {
        const state = useHRM2120_R01Store.getState();
        if (row.empNo === state.selectedHRM2120_R01?.empNo) return;
        confirmDiscard(() => {
            const current = useHRM2120_R01Store.getState();
            current.setSelectedHRM2120_R01(row);
            void loadDetails(row.empNo, current.HRM2120_R01PastFilter.baseDt);
        });
    }, [confirmDiscard, loadDetails]);

    /** 액션바 등록은 끝에, index의 +는 클릭한 배정 행 뒤에 추가한다. */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        const state = useHRM2120_R01Store.getState();
        if (!state.selectedHRM2120_R01) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }

        const newRow = {
            ...defaultHRM2120_R01DetailDto,
            empNo: state.selectedHRM2120_R01.empNo,
            startDt: getHRM2120_R01FirstDateOfMonth(),
            endDt: '',
            inserted: true,
        };
        if (rowIndex === undefined) {
            state.insertHRM2120_R01DetailList(newRow);
            return;
        }

        /** 공통 삽입 함수에 클릭한 행을 전달해 그 행 뒤에 신규 이력을 만든다. */
        InsertRowHandler({
            event,
            dataList: state.HRM2120_R01DetailList,
            rowData: state.HRM2120_R01DetailList[rowIndex],
            defaultRowData: newRow,
            insertRow: (rows, index) => {
                rows.forEach(row => state.insertHRM2120_R01DetailList(row, index));
            },
        });
    }, []);

    /** 신규 행은 제거하고 저장된 행은 삭제 표시하거나 복구한다. */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM2120_R01Store.getState();
        if (state.deletedHRM2120_R01DetailList.length) {
            state.setDeletedHRM2120_R01DetailList([]);
            state.setSelectedHRM2120_R01DetailList([]);
            return;
        }

        const selectedRowIds = state.selectedHRM2120_R01DetailList;
        const insertedRows = state.HRM2120_R01DetailList.filter(row =>
            row.inserted && selectedRowIds.includes(String(row.valueIndex)));
        const existingRowIds = selectedRowIds.filter(id => state.HRM2120_R01DetailList.some(row =>
            String(row.valueIndex) === id && !row.inserted));

        insertedRows.forEach(row => state.removeHRM2120_R01DetailList(row.valueIndex));
        state.setDeletedHRM2120_R01DetailList(existingRowIds);
        state.setSelectedHRM2120_R01DetailList([]);
    }, []);

    /** 선택 사원 한 명의 배정 전체를 검증해 PATCH로 저장한다. */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        detailGridRef.current?.api.stopEditing();
        const state = useHRM2120_R01Store.getState();
        const selectedEmployee = state.selectedHRM2120_R01;
        if (!selectedEmployee) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }

        const activeAssignments = state.HRM2120_R01DetailList.filter(row =>
            !state.deletedHRM2120_R01DetailList.includes(String(row.valueIndex))
            && !(row.inserted && !row.startDt));
        if (!hasChanges()) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }

        const error = validateHRM2120_R01ShiftList(
            selectedEmployee.empNm,
            activeAssignments,
            state.allWorkShiftCodes,
        );
        if (error) {
            showWarning(error);
            return;
        }

        setLoadingSpinner(true);
        try {
            const response = await patchHRM2120_R01({
                empNo: selectedEmployee.empNo,
                hrmEmpShiftDtoList: activeAssignments.map(row => ({
                    ...row,
                    endDt: row.endDt || END_DT_OPEN,
                })),
            });
            if (!response.data.successFlag) return;

            const message = '근무조 배정 저장이 완료되었습니다. 일일근태는 근무시간·휴게 기준 화면의 "일일근태 재계산"으로 다시 계산하세요.';
            showSuccess(message);
            setMessage(message);
            await executeDataFetch('refetch', selectedEmployee.empNo, true);
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, detailGridRef, hasChanges, setLoadingSpinner, setMessage, executeDataFetch]);

    /** 변경 폐기를 확인한 뒤 일괄 배정 확인창을 열고, 확인하면 즉시 저장한다. */
    const handleBulkApply = useCallback(() => {
        const state = useHRM2120_R01Store.getState();
        const form = state.HRM2120_R01BulkForm;
        if (!form.bulkStartDt) {
            showWarning('적용시작일을 고르세요.');
            return;
        }
        if (form.bulkType === 'FIX' && !form.bulkShiftCd) {
            showWarning('고정 근무조를 고르세요.');
            return;
        }
        if (!state.HRM2120_R01List.length) {
            showWarning('배정할 사원이 없습니다.');
            return;
        }

        confirmDiscard(() => {
            /** 확인된 사원 목록과 입력값을 서버에 보내고 조회 결과를 갱신한다. */
            const applyAssignment = async () => {
                setLoadingSpinner(true);
                try {
                    const response = await postHRM2120_R01Bulk({
                        ...form,
                        bulkEmpNoList: state.HRM2120_R01List.map(row => row.empNo),
                    });
                    if (!response.data.successFlag) return;

                    const count = response.data.data;
                    if (!count) {
                        showWarning('바뀐 배정이 없습니다.');
                        return;
                    }

                    const message = `조회된 ${count}명의 근무조를 ${form.bulkStartDt}부터 배정했습니다. 일일근태는 근무시간·휴게 기준 화면의 "일일근태 재계산"으로 다시 계산하세요.`;
                    showSuccess(message);
                    setMessage(message);
                    await executeDataFetch('refetch', state.selectedHRM2120_R01?.empNo, true);
                } finally {
                    setLoadingSpinner(false);
                }
            };

            setConfirmModalProps({
                title: '일괄 배정',
                message: `조회된 ${state.HRM2120_R01List.length}명에게 ${form.bulkStartDt}부터 ${BULK_TYPE_NAMES[form.bulkType]}을 배정합니다.`,
                confirmText: '적용',
                cancelText: '취소',
                onConfirm: () => {
                    closeConfirmModal();
                    void applyAssignment();
                },
                onCancel: closeConfirmModal,
            });
            setConfirmModalVisible(true);
        });
    }, [confirmDiscard, closeConfirmModal, setConfirmModalProps, setConfirmModalVisible, setLoadingSpinner, setMessage, executeDataFetch]);

    /** 선택 사원의 저장된 이력 Excel을 내려받는다. */
    const handleExcelClick = useCallback(async () => {
        const empNo = useHRM2120_R01Store.getState().selectedHRM2120_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }

        const response = await downloadExcel_HRM2120_R01(empNo);
        if (!response.data.successFlag) return;
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: `근무조 배정 이력_${empNo}-excel.xlsx`,
        });
    }, []);

    /** 화면 상태와 메시지를 초기화하고 근무조 선택 목록을 다시 채운다. */
    const initContainerData = useCallback(() => {
        useHRM2120_R01Store.getState().initData();
        setMessage('');
        void loadWorkShifts();
    }, [setMessage, loadWorkShifts]);

    return {
        fetchData: () => executeDataFetch('search'),
        handleHeaderRowClick,
        handleInsertClick,
        handleDeleteClick,
        handleSaveClick,
        handleBulkApply,
        handleExcelClick,
        initContainerData,
    };
};
